import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { type DraftTarget, type PageSection } from '@lib/customization/reportDesign';
import { useLocalization } from '@lib/localization';
import { Box, ButtonBase, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { type ReportBlock, type ReportChrome, type ReportDesignPage } from '@store/api/reportDesignEndpoints';
import { useLayoutEffect, useRef, useState } from 'react';
import { ArrowDownIcon, ArrowUpIcon, TrashIcon } from 'react-line-awesome';
import { issueDotStyles } from '../designer.styles';
import BlockFrame, { type BlockFrameActions } from './BlockFrame';
import {
  blockStackStyles, cutOffStyles, emptyPageLitStyles, emptyPageStyles, flaggedStyles, footStyles, navySheetStyles,
  overflowNoteStyles, pageBreakStyles, pageColumnStyles, pageSheetStyles, sheetGroupStyles, sheetHeaderStyles, sheetLitStyles,
  sheetNameStyles, sheetSelectedStyles, stripPlaceStyles,
} from './DesignCanvas.styles';
import { dragIds, type Placement } from './dragging';
import { A4, toneOf, type Print } from './print';
import { freePage } from './print.styles';
import Region from './Region';
import { ChromeStrip, type PageNumber } from './ReportParts';
import { usePointScale } from './usePointScale';
import { useReveal } from './useReveal';

/** What a page sheet can ask of the canvas. */
export interface PageSheetActions {
  select: (target: DraftTarget) => void;
  /** Moves the page within its section. */
  movePage: (pageId: string, to: number) => void;
  removePage: (pageId: string) => void;
  /** What a block's toolbar offers for it, each only where it is possible. */
  blockActions: (block: ReportBlock) => BlockFrameActions;
}

interface Props {
  page: ReportDesignPage;
  section: PageSection;
  /** Its place among the pages of its section, and how many of them there are. */
  index: number;
  count: number;
  /** "Cover page 2". */
  label: string;
  /**
   * The header and the footer, which a closing page takes unless it is navy. Left out for a
   * cover. `shown` on the one page whose strips are brought into view when the header or the
   * footer is pointed at, which is none while the results have a sheet to show them on.
   */
  chrome?: { header: ReportChrome; footer: ReportChrome; page: PageNumber; shown?: boolean };
  print: Print;
  canEdit: boolean;
  isSelected: (target: DraftTarget) => boolean;
  isFlagged: (target: DraftTarget) => boolean;
  /** What is to be scrolled into view, when it is on this sheet (`useReveal`). */
  shown: (target: DraftTarget) => number | undefined;
  /** Where the block that is being dragged would land, when that is on this page. */
  placement: Placement | null;
  actions: PageSheetActions;
}

const JUSTIFY = { top: 'flex-start', center: 'center', bottom: 'flex-end' } as const;

/**
 * What the canvas marks on a page whose blocks are `over` pixels taller than the `room`
 * between a page's margins, in pixels from the top of the blocks. A page that starts at the
 * top flows: a line wherever the report has filled a sheet. A centred or a bottom-aligned
 * page is one sheet, and the report prints the part of the blocks that its alignment leaves
 * on it: what is cut off above and below that is marked.
 */
const marksOf = (align: keyof typeof JUSTIFY, room: number, over: number): { breaks: number[]; cuts: Array<{ from: 'top' | 'bottom'; height: number }> } => {
  if (over <= 0 || room <= 0) return { breaks: [], cuts: [] };

  if (align === 'top') {
    return { breaks: Array.from({ length: Math.ceil(over / room) }, (_, sheet) => (sheet + 1) * room), cuts: [] };
  }

  return {
    breaks: [],
    cuts: align === 'bottom' ? [{ from: 'top', height: over }] : [{ from: 'top', height: over / 2 }, { from: 'bottom', height: over / 2 }],
  };
};

/**
 * A cover or a closing page as a sheet: the page as the report's free page draws it (white or
 * navy, its margins, its blocks from the top, centred or from the bottom), with the line over
 * it that names it and moves or deletes it.
 *
 * A page that holds more than a page of paper does is a taller sheet, with all its blocks on
 * it: one that was cut off as the report cuts it could not be taken hold of to put it right.
 * What the report makes of such a page is marked on the sheet and said under it. It continues
 * a page that starts at the top on another sheet, and cuts a centred or a bottom-aligned one.
 * The PDF tab shows the outcome.
 *
 * For the drag context the page is three things: a list its blocks are sorted in, a place to
 * drop on as a whole, and a foot under its last block for the keyboard to carry a block to.
 */
const PageSheet: React.FC<Props> = ({
  page, section, index, count, label, chrome, print, canEdit, isSelected, isFlagged, shown, placement, actions,
}) => {
  const { t } = useLocalization();
  const target: DraftTarget = { kind: 'page', id: page.id };
  const dark = page.background === 'navy';
  const tone = toneOf(dark);
  // A cover is centred and a closing page starts at the top, unless the page says otherwise.
  const align = page.verticalAlign ?? (section === 'covers' ? 'center' : 'top');
  const landing = placement?.pageId === page.id ? placement.before : undefined;

  const scale = usePointScale(A4.width);
  const whole = useDroppable({ id: dragIds.page(page.id), data: { kind: 'page', pageId: page.id } });
  const foot = useDroppable({ id: dragIds.foot(page.id), data: { kind: 'foot', pageId: page.id } });

  useReveal(whole.node, shown(target));

  // How much taller the blocks are than the room between the page's margins.
  const stack = useRef<HTMLDivElement>(null);
  const [over, setOver] = useState(0);
  const room = (A4.height - 2 * freePage.paddingY) * scale.pixels;
  const margin = freePage.paddingY * scale.pixels;
  const marks = marksOf(align, room, over);

  useLayoutEffect(() => {
    const element = stack.current;

    if (!element) return undefined;

    // Within a pixel a page fits: the browser rounds what it measures.
    const measure = () => setOver(element.offsetHeight > room + 1 ? Math.round(element.offsetHeight - room) : 0);

    measure();

    if (typeof ResizeObserver === 'undefined') return undefined;

    // A picture that arrives makes its block taller without anything being drawn again.
    const resized = new ResizeObserver(measure);

    resized.observe(element);

    return () => resized.disconnect();
  }, [room, page.blocks]);

  const action = (title: string, icon: React.ReactNode, onClick: () => void, off = false) => (
    <Tooltip title={title}>
      <span>
        <IconButton size="small" aria-label={title} disabled={off} onClick={onClick}>{icon}</IconButton>
      </span>
    </Tooltip>
  );

  return (
    <Box sx={sheetGroupStyles}>
      <Stack direction="row" sx={sheetHeaderStyles}>
        <ButtonBase
          sx={sheetNameStyles}
          aria-current={isSelected(target) || undefined}
          // Which page the name is of: it is handed the keyboard's focus when the page's last block is deleted (`useHandover`).
          data-page={page.id}
          onClick={() => actions.select(target)}
        >
          {label}
          {isFlagged(target) && <Box sx={issueDotStyles} />}
        </ButtonBase>
        <Box sx={{ flex: 1 }} />
        {canEdit && (
          <>
            {action(t('Customization_Designer_MoveUp'), <ArrowUpIcon />, () => actions.movePage(page.id, index - 1), index === 0)}
            {action(t('Customization_Designer_MoveDown'), <ArrowDownIcon />, () => actions.movePage(page.id, index + 1), index === count - 1)}
            {action(t('ReportDesign_Page_Delete'), <TrashIcon />, () => actions.removePage(page.id))}
          </>
        )}
      </Stack>

      <Box
        ref={scale.ref}
        style={scale.style}
        sx={[
          pageSheetStyles,
          dark && navySheetStyles,
          isFlagged(target) && flaggedStyles,
          isSelected(target) && sheetSelectedStyles,
          landing !== undefined && sheetLitStyles,
        ]}
      >
        <Box
          ref={whole.setNodeRef}
          sx={pageColumnStyles}
          style={{ justifyContent: JUSTIFY[align] ?? JUSTIFY.top }}
          onClick={() => actions.select(target)}
        >
          <Box ref={stack} sx={blockStackStyles}>
            <SortableContext items={page.blocks.map((block) => dragIds.block(block.id))} strategy={verticalListSortingStrategy}>
              {page.blocks.map((block, at) => {
                const blockTarget: DraftTarget = { kind: 'block', id: block.id };
                const last = at === page.blocks.length - 1;

                return (
                  <BlockFrame
                    key={block.id}
                    block={block}
                    pageId={page.id}
                    index={at}
                    count={page.blocks.length}
                    print={print}
                    dark={dark}
                    canEdit={canEdit}
                    selected={isSelected(blockTarget)}
                    flagged={isFlagged(blockTarget)}
                    first={at === 0}
                    line={landing === at ? 'above' : last && landing === at + 1 ? 'below' : undefined}
                    shown={shown(blockTarget)}
                    actions={actions.blockActions(block)}
                  />
                );
              })}
            </SortableContext>
            <Box ref={foot.setNodeRef} sx={footStyles} />

            {marks.breaks.map((top) => <Box key={top} sx={pageBreakStyles} style={{ top }} />)}
            {/* Out to the sheet's own edge, over the margin on that side. */}
            {marks.cuts.map(({ from, height }) => (
              <Box key={from} sx={cutOffStyles} style={{ [from]: -margin, height: height + margin }} />
            ))}
          </Box>
        </Box>

        {page.blocks.length === 0 && (
          <Box
            sx={landing === undefined ? emptyPageStyles : [emptyPageStyles, emptyPageLitStyles]}
            style={landing === undefined ? { color: tone.muted, borderColor: tone.hairline } : { color: tone.ink }}
          >
            <span>{t('ReportDesign_Canvas_EmptyPage')}</span>
            {canEdit && <span>{t('ReportDesign_Palette_Hint')}</span>}
          </Box>
        )}

        {chrome && !dark && (['header', 'footer'] as const).map((edge) => chrome[edge].enabled && (
          <Region
            key={edge}
            selected={isSelected({ kind: edge })}
            flagged={isFlagged({ kind: edge })}
            shown={chrome.shown ? shown({ kind: edge }) : undefined}
            sx={stripPlaceStyles[edge]}
            onSelect={() => actions.select({ kind: edge })}
          >
            <ChromeStrip chrome={chrome[edge]} edge={edge} print={print} page={chrome.page} />
          </Region>
        ))}
      </Box>

      {over > 0 && (
        <Typography variant="caption" sx={overflowNoteStyles}>
          {t(align === 'top' ? 'ReportDesign_Canvas_Overflow_Flows' : 'ReportDesign_Canvas_Overflow_Cut')}
        </Typography>
      )}
    </Box>
  );
};

export default PageSheet;
